import { FACILITY_DOCUMENTS } from "routes/Customer/Facility/Documents";
import { DOCUMENTS } from "routes/Customer/Documents";

import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { DialogType, useMinimizedDialogs } from "providers/minimizedDialogs";
import {
  CreateDocumentInput,
  DocumentType,
  ProcessingStrategy,
} from "generated-graphql/graphql";
import { useUploadFiles } from "hooks/useDocumentUpload";
import { useUpsertDocuments } from "hooks/useUpsertDocuments";
import { useAnalyzeDocuments } from "providers/documentAIAnalysis";
import compact from "lodash/compact";
import { useCallback, useState, useEffect, useRef } from "react";
import {
  DocumentFilePicker,
  DocumentPreviewDialog,
  useDocumentFiles,
} from "./shared";
export type AddEditDocumentWithAIEnrichmentDialogProps = {
  open: boolean;
  title?: string;
  onClose: () => void;

  /** Maximum number of files to upload. Defaults to 10 */
  maxFiles?: number;
  disabled?: boolean;
  tenantId: string;
};

export function AddEditDocumentWithAIEnrichmentDialog(
  props: AddEditDocumentWithAIEnrichmentDialogProps
) {
  const {
    tenantId,
    open,
    title = "Add Document(s)",
    onClose,
    maxFiles = 10,
    disabled,
  } = props;

  const alerts = useAlerts();
  const { createDialog } = useMinimizedDialogs();

  const [manualFillLoading, setManualFillLoading] = useState(false);

  // Store files ref for state capture (File objects can't be serialized)
  const filesRef = useRef<File[]>([]);

  const {
    files,
    canAddMore,
    handleDrop,
    removeFile,
    previewFile,
    openPreview,
    closePreview,
    previewUrl,
    resetFiles,
  } = useDocumentFiles({
    multiple: true,
    maxFiles,
  });

  // Update files ref when files change
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const uploadFiles = useUploadFiles();
  const { upsertDocuments } = useUpsertDocuments({
    refetchQueries: [FACILITY_DOCUMENTS, DOCUMENTS],
  });

  const { queueJob, queueLoading: queueDocumentAIEnrichmentJobLoading } =
    useAnalyzeDocuments();

  const onFillManually = useCallback(
    async (files: File[]) => {
      setManualFillLoading(true);
      try {
        // Upload files
        const keys = await uploadFiles(
          files.map((file) => ({
            file,
            documentData: { tenantId },
          }))
        );
        const storageLinks = compact(keys);
        if (storageLinks.length !== keys.length) {
          throw new Error("Some files failed to upload. Please try again.");
        }

        // Create basic document records without AI enrichment
        const documents = storageLinks.map((storageLink, i) => ({
          tenantId,
          title: files[i].name,
          fileExtension: files[i].name.split(".").pop()?.toLowerCase() ?? "",
          storageLink,
          documentType: DocumentType.Other,
          isArchived: false,
        }));

        const createdDocuments = await upsertDocuments({
          documents,
          currentSubmissionDocument: false,
        });

        if (createdDocuments && createdDocuments.length > 0) {
          // Documents will be set via the provider, which will trigger the review dialog
          // This is handled by the useEffect watching documentsToReview
        }
      } catch (error) {
        alerts.error("Something went wrong while uploading documents.", error);
      } finally {
        setManualFillLoading(false);
      }
    },
    [uploadFiles, tenantId, upsertDocuments, alerts]
  );

  const onUploadAndAutofill = useCallback(
    async (files: File[]) => {
      try {
        const keys = await uploadFiles(
          files.map((file) => ({
            file,
            documentData: { tenantId },
          }))
        );
        const storageLinks = compact(keys);
        if (storageLinks.length !== keys.length) {
          throw new Error("Some files failed to upload. Please try again.");
        }

        const documentData: CreateDocumentInput[] = storageLinks.map(
          (storageLink, i) => ({
            storageLink,
            title: files[i].name,
            fileExtension: files[i].name.split(".").pop()?.toLowerCase() ?? "",
          })
        );

        const newJobId = await queueJob({
          documents: documentData,
          tenantId,
          strategy: ProcessingStrategy.ExtractNewEmbedNew,
          refetchQueries: [FACILITY_DOCUMENTS, DOCUMENTS],
        });

        if (!newJobId) {
          throw new Error("Failed to get job ID from queue response");
        }

        // Show the analyzing dialog
        const dialogId = `document-ai-analyzing-${newJobId}`;
        createDialog({
          id: dialogId,
          dialogType: DialogType.DocumentAIAnalyzing,
          props: {
            open: true,
            jobId: newJobId,
            preferOriginalTitle: true,
            dialogId, // Pass dialogId so DocumentAIAnalyzingDialog uses the same ID when minimizing
          },
        });
      } catch (error) {
        alerts.error(
          "Something went wrong while uploading and beginning the document analysis process.",
          error
        );
        resetFiles();
      } finally {
        //close the dialog
        if (onClose) {
          onClose();
        }
      }
    },
    [uploadFiles, tenantId, queueJob, alerts, resetFiles, createDialog, onClose]
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          resetFiles();
          onClose();
        }}
        fullWidth
        maxWidth="sm"
        title={title}
        cancelButtonProps={{
          onClick: () => onFillManually(files),
          disabled: disabled || files.length === 0 || manualFillLoading,
          text: "Fill Manually Instead",
          loading: manualFillLoading,
        }}
        primaryActionButtonProps={{
          hideStartIcon: true,
          onClick: () => onUploadAndAutofill(files),
          disabled: disabled || files.length === 0 || manualFillLoading,
          saveText: "Upload & Autofill",
          loading: queueDocumentAIEnrichmentJobLoading,
        }}
      >
        <DocumentFilePicker
          files={files}
          onDrop={handleDrop}
          onRemove={removeFile}
          onPreview={openPreview}
          canAddMore={canAddMore}
          multiple
          maxFiles={maxFiles}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tif,.tiff,.pub,.vsd"
          disabled={disabled || manualFillLoading}
          helperText={`Drag and drop or select up to ${maxFiles} files (PDFs, images, or office documents). We'll try to detect the type of each document and fill in the details for you.`}
        />
      </Dialog>

      {previewFile && (
        <DocumentPreviewDialog
          open={Boolean(previewFile)}
          onClose={closePreview}
          fileName={previewFile.name}
          previewUrl={previewUrl}
        />
      )}
    </>
  );
}
